import React, { useEffect, useRef, useState } from 'react';
import { Bookmark } from 'lucide-react';
import { useContent } from '../contexts/ContentContext';
import { useIllustrationIndex } from '../hooks/useIllustrationIndex';
import { useVisitorFavorites } from '../hooks/useVisitorFavorites';
import { useArchiveYears } from '../hooks/useArchiveYears';
import { IllustrationRoot, illustrationRoots, loadIllustrationCondition } from '../services/illustrations';
import { illustrationUrl } from '../utils/illustrationRoute';
import { FavoriteImage } from '../utils/visitorFavorites';
import { getConfig } from '../config';
import ExploreSection from './ExploreSection';
import GuideHelpButton from './GuideHelpButton';
import { attributeGuideSections } from '../utils/guideHelp';

type Candidate = { value: string; label: string; count?: number; images?: FavoriteImage[]; thumb?: string; href?: string };

function CandidateCard({ root, candidate }: { root: IllustrationRoot; candidate: Candidate }) {
    const { t } = useContent();
    const ref = useRef<HTMLDivElement>(null);
    const [thumb, setThumb] = useState(candidate.thumb || '');
    const [count, setCount] = useState(candidate.count);
    const [error, setError] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const saved = JSON.stringify(candidate.images);
    useEffect(() => {
        if (root === 'recent') return;
        const controller = new AbortController();
        setThumb(''); setCount(candidate.count); setError(false);
        const observer = new IntersectionObserver(entries => {
            if (!entries.some(entry => entry.isIntersecting)) return;
            observer.disconnect();
            loadIllustrationCondition(root, candidate.value, controller.signal, saved ? JSON.parse(saved) : undefined).then(result => {
                if (!controller.signal.aborted) { setThumb(result.items[0]?.thumb || ''); setCount(result.total); }
            }).catch(() => { if (!controller.signal.aborted) setError(true); });
        }, { rootMargin: '200px' });
        if (ref.current) observer.observe(ref.current);
        return () => { controller.abort(); observer.disconnect(); };
    }, [root, candidate.value, candidate.count, saved, attempt]);
    return <div ref={ref} className="lab-discovery-candidate">
        <a href={candidate.href || illustrationUrl(root, candidate.value)}>
            <span className="lab-collection-cover" aria-hidden="true">{thumb ? <img src={new URL(thumb, new URL(getConfig().apiBaseUrl).origin).href} alt="" loading="lazy" onError={() => setThumb('')} /> : <Bookmark />}</span>
            <span>{candidate.label}{count !== undefined && ` (${count})`}</span>
        </a>
        {error && <button type="button" className="lab-link" onClick={() => setAttempt(value => value + 1)} aria-label={t('common.retry') + ': ' + candidate.label}>{t('common.retry')}</button>}
    </div>;
}

function CandidateRow({ root, candidates, href }: { root: IllustrationRoot; candidates: Candidate[]; href?: string }) {
    const { t } = useContent();
    const help = attributeGuideSections[root];
    return <ExploreSection root={root} className="lab-discovery-route" href={href || illustrationUrl(root)}
        headerContent={help && <GuideHelpButton section={help} />}
        title={t(root === 'recent' ? 'illustrations.allByYear' : `illustrations.${root}`)}>
        {() => candidates.length ? candidates.map(candidate => <CandidateCard key={candidate.value} root={root} candidate={candidate} />) : <p>{t('illustrations.noValues')}</p>}
    </ExploreSection>;
}

export default function DiscoveryCandidates() {
    const { t } = useContent();
    const { data, error, retry } = useIllustrationIndex();
    const favorites = useVisitorFavorites();
    const archive = useArchiveYears();
    if (error) return <div role="alert"><p>{t('search.error')}</p><button className="lab-button" onClick={retry}>{t('common.retry')}</button></div>;
    if (!data) return <p role="status">{t('common.loading')}</p>;
    return <div className="lab-discovery-candidates">
        {archive.error && <p role="alert">{t('search.error')} <button className="lab-button" onClick={archive.retry}>{t('common.retry')}</button></p>}
        {favorites.error && <p role="alert">{favorites.error}</p>}
        {illustrationRoots.filter(root => root !== 'popular').map(root => {
            if (root === 'recent' && archive.loading) return <p key={root} role="status">{t('common.loading')}</p>;
            const candidates: Candidate[] = root === 'recent' ? archive.years.filter(year => year.href).map(year => ({ value: year.year, label: year.year, count: year.count, thumb: year.cover_thumb, href: year.href! }))
                : root === 'rating' ? data.ratings.filter(item => item.count > 0).sort((a, b) => b.value - a.value).map(item => ({ value: String(item.value), label: `★${item.value}`, count: item.count }))
                : root === 'favorites' ? favorites.readable ? [
                    { value: '', label: t('illustrations.all'), images: favorites.data.images },
                    { value: 'unfiled', label: t('illustrations.unfiled'), images: favorites.data.images.filter(item => !item.collectionIds.length) },
                    ...favorites.data.collections.map(collection => ({ value: collection.id, label: collection.name, images: favorites.data.images.filter(item => item.collectionIds.includes(collection.id)) }))
                ] : [] : (data.groups[root]?.values || []).filter(item => item.count > 0).map(item => ({ value: item.slug, label: item.label, count: item.count }));
            return <CandidateRow key={root} root={root} candidates={candidates} href={root === 'recent' ? archive.latestHref || undefined : undefined} />;
        })}
    </div>;
}
